export interface Coupon {
  id: string
  category: string
  title: string
  description: string
  code: string
  terms: string
  /** Inclusive local calendar date, YYYY-MM-DD, in America/Chicago. */
  expiresAt: string | null
  status: 'active' | 'inactive'
  inStore: boolean
  ctaLabel: string
  ctaHref: string
}

export const offerTerms = 'Coupons and promotions are subject to availability and may be modified or discontinued at any time. Unless otherwise stated, offers cannot be combined. Carrier charges, postage, taxes, mailbox fees and third-party fees are excluded from discounts.'

export const coupons: readonly Coupon[] = [
  {
    id: 'business-cards-20', category: 'Printing', title: '20% Off Business Cards',
    description: 'Save 20% on your first qualifying business card order.', code: 'CARDS20',
    terms: 'New customers or first business-card order only. Minimum order may apply. Cannot be combined with other offers.',
    expiresAt: null, status: 'active', inStore: true, ctaLabel: 'Use This Offer', ctaHref: '/contact-us',
  },
  {
    id: 'print-order-10', category: 'Printing', title: '$10 Off Print Orders of $50+',
    description: 'Save $10 when your qualifying print order totals $50 or more before tax.', code: 'PRINT10',
    terms: 'Applies to eligible in-store print services. Excludes shipping charges, postage, mailbox services, taxes and third-party fees.',
    expiresAt: null, status: 'active', inStore: true, ctaLabel: 'Use This Offer', ctaHref: '/contact-us',
  },
  {
    id: 'flyers-25', category: 'Marketing Materials', title: 'Buy 100 Flyers, Get 25 Free',
    description: 'Get 25 additional flyers at no charge with a qualifying 100-flyer order.', code: 'FLYER25',
    terms: 'Standard sizes and eligible paper stocks only. Design fees, rush charges and specialty finishes excluded.',
    expiresAt: null, status: 'active', inStore: true, ctaLabel: 'Use This Offer', ctaHref: '/contact-us',
  },
  {
    id: 'packing-5', category: 'Packing & Shipping', title: '$5 Off Professional Packing',
    description: 'Save $5 on professional packing when purchased with a qualifying shipment.', code: 'PACK5',
    terms: 'Packing service only. Shipment must be processed through Pack Print Mail. Excludes carrier charges and packaging purchased without packing service.',
    expiresAt: null, status: 'active', inStore: true, ctaLabel: 'Use This Offer', ctaHref: '/contact-us',
  },
  {
    id: 'marketing-consultation', category: 'Marketing & Websites', title: 'Free 30-Minute Business Marketing Consultation',
    description: 'Talk with us about your website, SEO, Google Business Profile, social media, advertising, branding, printing and other business-growth needs.', code: 'GROW30',
    terms: 'One consultation per business. Appointment may be required. No purchase necessary.',
    expiresAt: null, status: 'active', inStore: false, ctaLabel: 'Book a Consultation', ctaHref: '/digital-marketing/custom',
  },
  {
    id: 'business-starter', category: 'Business Services', title: 'New Business Starter Offer',
    description: 'Ask us about bundled pricing for business cards, flyers, branding, website setup and digital marketing services.', code: 'START',
    terms: 'Custom quote required. Pricing depends on selected products and services.',
    expiresAt: null, status: 'active', inStore: false, ctaLabel: 'Request a Quote', ctaHref: '/digital-marketing/custom',
  },
]

function isCalendarDate(value: string) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false
  const parsed = new Date(`${value}T00:00:00Z`)
  return Number.isFinite(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value
}

export function getActiveCoupons(items: readonly Coupon[], now = new Date()) {
  const today = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Chicago', year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(now)
  return items.filter((coupon) => coupon.status === 'active' && (
    coupon.expiresAt === null || (isCalendarDate(coupon.expiresAt) && coupon.expiresAt >= today)
  ))
}

export function formatCouponExpiration(date: string) {
  return new Intl.DateTimeFormat('en-US', { timeZone: 'UTC', month: 'long', day: 'numeric', year: 'numeric' }).format(new Date(`${date}T00:00:00Z`))
}
